import { useState, useEffect } from "react";

const ASPECT_DIMENSIONS = {
  widescreen: [640, 360],
  vertical: [640, 1138],
  square: [640, 640],
  unknown: [640, 640],
};

const BrandedFallback = ({ project, testIdPrefix }) => (
  <div
    data-testid={testIdPrefix ? `${testIdPrefix}-fallback` : "media-box-branded-fallback"}
    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#151515] text-center px-5 border border-[#2A2A2A]"
  >
    <img data-testid={testIdPrefix ? `${testIdPrefix}-fallback-logo` : undefined} src="/eipi-logo.png" alt="" className="w-8 h-8 opacity-50 object-contain" />
    <span data-testid={testIdPrefix ? `${testIdPrefix}-fallback-title` : undefined} className="text-sm sm:text-base tracking-tight text-[#F5F5F0] truncate max-w-full">{project.title}</span>
    {project.category && (
      <span data-testid={testIdPrefix ? `${testIdPrefix}-fallback-category` : undefined} className="text-[10px] uppercase tracking-widest text-[#888880] truncate max-w-full">{project.category}</span>
    )}
    <span data-testid={testIdPrefix ? `${testIdPrefix}-fallback-year` : undefined} className="text-[10px] uppercase tracking-widest text-[#CCFF00]/70">{project.year || "EiPi Media"}</span>
  </div>
);

// Fixed dimensions + lazy loading + one automatic retry (cache-busted) before falling back to the
// bundled local BrandedFallback - never a blank card or a broken-image icon.
export const MediaBox = ({ project, className = "relative w-full h-full", priority = false, onImgLoad, fit = "contain", testIdPrefix }) => {
  const [retryCount, setRetryCount] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setRetryCount(0);
    setFailed(false);
  }, [project.posterUrl]);

  const handleError = () => {
    if (retryCount < 1) {
      setRetryCount((n) => n + 1);
    } else {
      setFailed(true);
    }
  };

  const showFallback = !project.posterUrl || failed;
  const [w, h] = ASPECT_DIMENSIONS[project.coverLayout] || ASPECT_DIMENSIONS.unknown;
  const src = project.posterUrl
    ? retryCount > 0
      ? `${project.posterUrl}${project.posterUrl.includes("?") ? "&" : "?"}retry=${retryCount}`
      : project.posterUrl
    : null;

  return (
    <div className={`${className} bg-black overflow-hidden`}>
      {!showFallback ? (
        <img
          key={retryCount}
          src={src}
          width={w}
          height={h}
          alt={`${project.title} — ${project.brand}`}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={onImgLoad}
          onError={handleError}
          data-testid={testIdPrefix ? `${testIdPrefix}-image` : "media-box-image"}
          className={`absolute inset-0 w-full h-full ${fit === "cover" ? "object-cover" : "object-contain"}`}
        />
      ) : (
        <BrandedFallback project={project} testIdPrefix={testIdPrefix} />
      )}
    </div>
  );
};

export default MediaBox;
